import React, { useState, useMemo } from 'react';

import { Box } from '@mui/material';
import { Table, createColumnHelper, useTableSelection } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { InfoProgramModal } from 'shared/modals/InfoProgramModal/InfoProgramModal';
import { useGetOneProgram } from 'hooks/programs/useGetOneProgram';

import {
  IProgram,
  ITableProgramProps,
  TableProgramData,
} from 'interfaces/program/programInterfaces';
import { tableCellStyle, tableCellRowContentStyle } from 'ui/tables/tables';
import { useSnackbar } from 'notistack';
import { ErrorData } from 'interfaces/global/globalInterfaces';

const columnHelper = createColumnHelper<TableProgramData>();

export const TableProgram: React.FC<ITableProgramProps> = ({
  programsData,
  selectedPrograms,
  handleSelectedPrograms,
  handleActionsWithProgramsTooltip,
}) => {
  const [isInfoProgramModal, setInfoProgramModal] = useState(false);
  const [programId, setProgramId] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoadingGetOneProgram,
    isErrorGetOneProgram,
    programData,
  } = useGetOneProgram({
    programId,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const programArr: IProgram[] = useMemo(() => programsData || [], [programsData]);

  const rows: TableProgramData[] = useMemo(
    () =>
      (programArr || []).map((item: IProgram) => ({
        title: item.programTitle,
        service_types: item.serviceTypes.length > 0 ? `${item.serviceTypes[0]?.title} (+${item.serviceTypes.length - 1})` : '0',
        city: item.city,
        slot_budget: item.budget,
        service_period: `${item.paidSlots}`,
        slots_count: item.slotsCount,
        files: 'Мед. программа',
        programUuid: item.programUuid,
        medicalProgram: item.medicalProgram,
      })),
    [programArr]
  );

  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: setSorting,
    };
  }, [sorting]);

  // Row Selection
  const selection = useTableSelection<TableProgramData>({
    idField: 'programUuid',
  });

  // Синхронизируем selection с выбранными программами через callback
  const prevSelectionRef = React.useRef<Record<string, boolean>>({});
  
  React.useEffect(() => {
    const currentSelection = selection.rowSelection;
    const prevSelection = prevSelectionRef.current;
    
    // Проверяем, изменилась ли selection
    const currentKeys = Object.keys(currentSelection).filter((key) => currentSelection[key]);
    const prevKeys = Object.keys(prevSelection).filter((key) => prevSelection[key]);
    
    if (
      currentKeys.length !== prevKeys.length ||
      currentKeys.some((key) => !prevKeys.includes(key)) ||
      prevKeys.some((key) => !currentKeys.includes(key))
    ) {
      const selectedUuids = currentKeys;
      const currentUuids = selectedPrograms as string[];
      
      // Обновляем только если действительно изменилось
      if (
        selectedUuids.length !== currentUuids.length ||
        selectedUuids.some((uuid) => !currentUuids.includes(uuid))
      ) {
        handleSelectedPrograms(selectedUuids as readonly string[]);
      }
    }
    
    prevSelectionRef.current = currentSelection;
  }, [selection.rowSelection, selectedPrograms, handleSelectedPrograms]);

  const handleInfoProgramModalOpen = (): void => {
    setInfoProgramModal(true);
  };

  const handleInfoProgramModalClose = (): void => {
    setProgramId(null);
    setInfoProgramModal(false);
  };

  // Форматирование числа с пробелами
  const formatNumber = (num: number): string => {
    return num
                          .toFixed(2)
                          .split('')
                          .reverse()
                          .join('')
                          .replace(/\d\d\d/g, '$& ')
                          .split('')
                          .reverse()
      .join('');
  };

  // Колонки таблицы
  const columns = useMemo<ColumnDef<TableProgramData, any>[]>(
    () => [
      columnHelper.accessor('title', {
        id: 'title',
        header: () => 'Программа',
        cell: (info) => (
          <Box sx={tableCellStyle} component="span">
            {info.getValue()}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
      columnHelper.accessor('service_types', {
        id: 'service_types',
        header: () => 'Объем услуг',
        cell: (info) => (
          <Box sx={tableCellStyle} component="span">
            {info.getValue()}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
      columnHelper.accessor('city', {
        id: 'city',
        header: () => 'Город обсл.',
        cell: (info) => (
          <Box sx={tableCellStyle} component="span">
            {info.getValue()}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
      columnHelper.accessor('slot_budget', {
        id: 'slot_budget',
        header: () => 'Стоимость, ₽',
        cell: (info) => (
          <Box sx={tableCellStyle} component="span">
            {formatNumber(info.getValue())}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
      columnHelper.accessor('slots_count', {
        id: 'slots_count',
        header: () => 'Кол-во, чел.',
        cell: (info) => (
          <Box sx={tableCellStyle} component="span">
            {info.getValue()}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
      columnHelper.accessor('files', {
        id: 'files',
        header: () => 'Файлы',
        size: 200,
        minSize: 180,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Box sx={tableCellRowContentStyle}>
                        <Box
                          component="div"
                          onClick={(evt) => {
                            evt.stopPropagation();
                  window.open(row.medicalProgram);
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                    whiteSpace: 'nowrap',
                            }}
                          >
                            <DescriptionIcon
                              sx={{
                                marginleft: '6px',
                                width: '14px',
                                height: '14px',
                                color: '#BCBEC8',
                              }}
                            />
                  {row.files}
                          </Box>
                        </Box>

                        <IconButton
                aria-label="info"
                          onClick={(evt) => {
                            evt.stopPropagation();
                  setProgramId(row.programUuid);
                            handleActionsWithProgramsTooltip(false);
                            handleInfoProgramModalOpen();
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: '#BCBEC8' }} />
                        </IconButton>
            </Box>
          );
        },
        enableSorting: true,
      }) as ColumnDef<TableProgramData, any>,
    ],
    [handleActionsWithProgramsTooltip]
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '20px',
          width: '100%',
        }}
      >
        <Table
          data={rows}
          columns={columns}
          selectionProps={selection}
          sorting={tableSorting}
          enableSorting
        />
      </Box>

      {!isLoadingGetOneProgram && !isErrorGetOneProgram && (
        <InfoProgramModal
          open={isInfoProgramModal}
          programData={programData}
          handleClose={handleInfoProgramModalClose}
        />
      )}
    </>
  );
};
